import React, { useState } from 'react';
import { View, StyleSheet, TouchableOpacity, ScrollView } from 'react-native';
import { Text } from '@/components/common/text';
import { SVG_COMPONENTS } from '@/assets/admin';
import { GlassFormInput } from '@/components/admin/redesign';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { FEATURE_FLAG_CONFIG, getFeatureFlagValue } from '../constant';
import { useFeatureFlagStyles, getValueStyleKey } from '../utils/featureFlagStyles';

interface FlagDetailsContentProps {
  localFlags: Record<string, any>;
  editingKey: string | null;
  editValue: string;
  setEditValue: (value: string) => void;
  onSubmitValue: (key: string) => void;
  onCancelEditing: () => void;
  onStartEditing: (key: string, currentValue: any) => void;
  onResetFlag: (key: string) => void;
}

export const FlagDetailsContent: React.FC<FlagDetailsContentProps> = ({
  localFlags,
  editingKey,
  editValue,
  setEditValue,
  onSubmitValue,
  onCancelEditing,
  onStartEditing,
  onResetFlag,
}) => {
  const { redesign, semantic, admin } = useAdminColors();
  const sharedStyles = useFeatureFlagStyles();
  const [expandedFlags, setExpandedFlags] = useState<Set<string>>(new Set());

  const toggleFlag = (key: string) => {
    setExpandedFlags((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  return (
    <ScrollView
      style={styles.content}
      contentContainerStyle={styles.contentContainer}
      showsVerticalScrollIndicator={false}
      keyboardShouldPersistTaps="handled"
    >
      {Object.entries(FEATURE_FLAG_CONFIG).map(([key, config]) => {
        const currentValue = getFeatureFlagValue(key, localFlags);
        const isCustomValue = key in localFlags;
        const isExpanded = expandedFlags.has(key);
        const valueStyleKey = getValueStyleKey(currentValue, config.defaultValue);

        return (
          <View
            key={key}
            style={[
              styles.flagCard,
              { backgroundColor: admin.dataTable.row },
            ]}
          >
            <TouchableOpacity style={styles.flagHeader} onPress={() => toggleFlag(key)}>
              <Text style={[styles.flagTitle, { color: redesign.text.primary }]}>{config.key}</Text>
              {isExpanded ? (
                <SVG_COMPONENTS.CHEVRON_DOWN
                  width={16}
                  height={16}
                  color={redesign.text.secondary}
                />
              ) : (
                <SVG_COMPONENTS.CHEVRON_RIGHT
                  width={16}
                  height={16}
                  color={redesign.text.secondary}
                />
              )}
            </TouchableOpacity>

            {isExpanded && (
              <View style={[styles.flagDetails, { borderTopColor: redesign.card.border }]}>
                <View style={styles.detailRow}>
                  <Text style={[styles.detailLabel, { color: redesign.text.secondary }]}>
                    Description:
                  </Text>
                  <Text style={[styles.detailValue, { color: redesign.text.primary }]}>
                    {config.description}
                  </Text>
                </View>

                <View style={styles.detailRow}>
                  <Text style={[styles.detailLabel, { color: redesign.text.secondary }]}>
                    Current Value: {!isCustomValue && '(Using Default)'}
                  </Text>
                  <View style={[sharedStyles.valueBadge, sharedStyles[valueStyleKey]]}>
                    <Text style={sharedStyles.valueBadgeText}>{currentValue?.toString()}</Text>
                  </View>
                </View>

                <View style={styles.detailRow}>
                  <Text style={[styles.detailLabel, { color: redesign.text.secondary }]}>
                    Default Value:
                  </Text>
                  <View style={[sharedStyles.valueBadge, sharedStyles.valueBadgeDefault]}>
                    <Text style={sharedStyles.valueBadgeText}>{config.defaultValue.toString()}</Text>
                  </View>
                </View>

                {editingKey === key ? (
                  <View style={styles.editForm}>
                    <GlassFormInput
                      value={editValue}
                      onChangeText={setEditValue}
                      placeholder={`Enter value for ${key}`}
                      autoFocus
                    />
                    <View style={styles.editButtons}>
                      <TouchableOpacity
                        style={[
                          styles.button,
                          { backgroundColor: semantic.interactive.primary },
                        ]}
                        onPress={() => onSubmitValue(key)}
                      >
                        <Text style={styles.buttonTextSolid}>Save</Text>
                      </TouchableOpacity>
                      <TouchableOpacity
                        style={[
                          styles.button,
                          {
                            backgroundColor: redesign.glass.buttonBg,
                            borderColor: redesign.card.border,
                            borderWidth: 1,
                          },
                        ]}
                        onPress={onCancelEditing}
                      >
                        <Text style={[styles.buttonTextGlass, { color: redesign.text.primary }]}>
                          Cancel
                        </Text>
                      </TouchableOpacity>
                    </View>
                  </View>
                ) : (
                  <View style={styles.actionButtons}>
                    <TouchableOpacity
                      style={[
                        styles.button,
                        {
                          backgroundColor: redesign.glass.buttonBg,
                          borderColor: redesign.card.border,
                          borderWidth: 1,
                        },
                      ]}
                      onPress={() => onResetFlag(key)}
                    >
                      <Text style={[styles.buttonTextGlass, { color: redesign.text.primary }]}>
                        Restore Default
                      </Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[
                        styles.button,
                        { backgroundColor: semantic.interactive.primary },
                      ]}
                      onPress={() => onStartEditing(key, currentValue)}
                    >
                      <Text style={styles.buttonTextSolid}>Set Custom Value</Text>
                    </TouchableOpacity>
                  </View>
                )}
              </View>
            )}
          </View>
        );
      })}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  content: {
    flex: 1,
  },
  contentContainer: {
    paddingBottom: 32,
  },
  flagCard: {
    borderRadius: 8,
    marginBottom: 12,
    overflow: 'hidden',
  },
  flagHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
  },
  flagTitle: {
    fontSize: 16,
    fontWeight: '500',
    flex: 1,
  },
  flagDetails: {
    padding: 16,
    paddingTop: 8,
    borderTopWidth: 1,
  },
  detailRow: {
    marginBottom: 16,
  },
  detailLabel: {
    fontSize: 14,
    marginBottom: 8,
  },
  detailValue: {
    fontSize: 14,
  },
  editForm: {
    marginTop: 8,
  },
  editButtons: {
    flexDirection: 'row',
    gap: 8,
  },
  actionButtons: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 8,
  },
  button: {
    flex: 1,
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 4,
    alignItems: 'center',
  },
  buttonTextSolid: {
    fontSize: 14,
    fontWeight: '500',
    color: '#FFFFFF',
  },
  buttonTextGlass: {
    fontSize: 14,
    fontWeight: '500',
  },
});
